import './AddTeamMember.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { Select } from '../../../../design/inputs/select/Select';
import { AddIcon } from '../../../../design/static/icons/AddIcon';
import { ValidatedTextInput } from '../../../components/ValidatedTextInput';
import { DisabledWhenPolicyExceeded } from '../../../containers/DisabledWhenPolicyExceeded';
import { EnableFor } from '../../../containers/EnableForRole';
import { InputStatus } from '../../../models/error.model';
import { Plan } from '../../user/user.store';
import { WorkspaceRole } from '../../workspaces/workspaces.store';
import { useTeamPermissions } from '../team.permissions';

interface IAddTeamMemberProps {
  currentWorkspaceId: string;
  email: string;
  emailStatus: InputStatus;
  selectedRole: WorkspaceRole;
  errorContext: { plan?: Plan };
  onRoleSelected: (role: WorkspaceRole) => void;
  onAddNewUser: () => Promise<void>;
  onEmailChange: (email: string) => void;
  clearErrors: () => void;
}

interface IState {
  workspaceId: string;
  selectedRole: WorkspaceRole;
}

export const rolesPossibleToAssign: WorkspaceRole[] = [
  WorkspaceRole.Manager,
  WorkspaceRole.Trainer,
  WorkspaceRole.Developer,
  WorkspaceRole.Collaborator,
  WorkspaceRole.Guest,
  WorkspaceRole.Assistant,
  WorkspaceRole.Worker,
];

export const AddTeamMember = observer((props: IAddTeamMemberProps): ReactElement => {
  const { t } = useTranslator();
  const teamPermissions = useTeamPermissions();

  const [state, setState] = useState({
    workspaceId: '',
    selectedRole: WorkspaceRole.Collaborator,
  });

  useEffect(() => {
    const newState: Partial<IState> = { selectedRole: props.selectedRole };

    if (props.currentWorkspaceId && props.email && props.currentWorkspaceId !== state.workspaceId) {
      props.clearErrors();
      newState.workspaceId = props.currentWorkspaceId;
    }

    if (
      (state.selectedRole === WorkspaceRole.Worker && props.selectedRole !== WorkspaceRole.Worker) ||
      (state.selectedRole !== WorkspaceRole.Worker && props.selectedRole === WorkspaceRole.Worker)
    ) {
      props.clearErrors();
    }

    setState({ ...state, ...newState });
  }, [props]);

  const options = rolesPossibleToAssign.map((r) => ({
    value: r,
    label: t(r),
  }));

  return (
    <div className='add-team-member'>
      <div className='input-wrapper'>
        <DisabledWhenPolicyExceeded limitType='user'>
          <ValidatedTextInput
            id='new-user-email-input'
            value={props.email}
            onChange={props.onEmailChange}
            feedbacks={props.emailStatus.errorCodes}
            feedbacksParams={{ plan: t(props.errorContext.plan || 'plan') }}
            isValid={props.emailStatus.isValid}
            placeholder={t('email')}
          />
        </DisabledWhenPolicyExceeded>
      </div>
      <div className='input-wrapper'>
        <DisabledWhenPolicyExceeded limitType='user'>
          <Select
            id='user-workspace-role-dropdown'
            value={options.find((x) => x.value === props.selectedRole)}
            onChange={(_, v) => {
              props.onRoleSelected(v.value ?? WorkspaceRole.None);
            }}
            options={options}
          />
        </DisabledWhenPolicyExceeded>
      </div>

      <DisabledWhenPolicyExceeded limitType='user'>
        <EnableFor hasPermission={teamPermissions.canAddTeamMembers()}>
          <ButtonWithLoader
            id='add-user-button'
            onClick={() => {
              void props.onAddNewUser();
            }}
            disabled={props.emailStatus.isValid !== true}
            loaderKey='add-user-button'
            startDecorator={<AddIcon />}
          >
            {t('add')}
          </ButtonWithLoader>
        </EnableFor>
      </DisabledWhenPolicyExceeded>
    </div>
  );
});
